import type { ChatAttachment } from '@/lib/chatAttachments';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAskCannafax } from '@/contexts/AskCannafaxContext';
import { cn } from '@/lib/utils';
import { History, Loader2, Plus, Sparkles, Trash2, X } from 'lucide-react';
import { useAskCannafaxChat } from './useAskCannafaxChat';
import { useChatSearch } from './useChatSearch';
import { ChatSearchInput, ChatSearchResults } from './ChatSearchResults';
import { deskMoveCopy, moveStarters, NEXT_MOVE_LABEL, NOT_NOW } from '@/lib/deskNextMove';
import { useNextMove } from '@/hooks/useNextMove';
import { withCue } from '@/lib/spotlightCue';
import { trackActivity } from '@/lib/activityTracking';
import DeskMark from '@/components/desk/DeskMark';
import { getPageSuggestions } from './pageSuggestions';
import { AskCannafaxConversation } from './AskCannafaxConversation';
import { AskCannafaxComposer } from './AskCannafaxComposer';

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

import { SuggestionTray } from './SuggestionTray';
import { hasAnswer, pickFollowUps, pickStarters } from '@/lib/askFollowUps';
import { moveDestination } from '@/lib/deskReply';

export function AskCannafaxPanel() {
  const { isOpen, setIsOpen, pendingPrompt, consumePendingPrompt, pinnedMove, clearPinnedMove } = useAskCannafax();
  const { dismiss, recordClick } = useNextMove(false);
  const location = useLocation();
  const chat = useAskCannafaxChat(isOpen);
  const [input, setInput] = useState('');
  const [chatQuery, setChatQuery] = useState('');
  const chatSearch = useChatSearch(chatQuery);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const suggestions = useMemo(
    () => getPageSuggestions(location.pathname),
    [location.pathname],
  );

  const focusComposer = () => {
    window.setTimeout(() => textareaRef.current?.focus(), 80);
  };

  // Focus on open, after thread switch, and once streaming completes.
  useEffect(() => {
    if (isOpen) focusComposer();
  }, [isOpen, chat.activeThreadId]);

  useEffect(() => {
    if (isOpen && !chat.isStreaming) focusComposer();
  }, [chat.isStreaming, isOpen]);

  // An "Ask about this" action queued a question: open on a fresh thread and send it.
  useEffect(() => {
    if (!isOpen || !pendingPrompt || chat.isStreaming) return;
    const prompt = consumePendingPrompt();
    if (!prompt) return;
    chat.startNewChat();
    setInput('');
    void chat.send(prompt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, pendingPrompt, chat.isStreaming]);

  // Opened from a Your Next Move™ / Desk Setup pill: fresh conversation, move pinned on top.
  const pinnedKey = pinnedMove && pinnedMove.kind !== 'caught_up' ? pinnedMove.key : null;
  useEffect(() => {
    if (!isOpen || !pinnedKey) return;
    chat.startNewChat();
    setInput('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, pinnedKey]);

  const pinned = pinnedMove && pinnedMove.kind !== 'caught_up' ? pinnedMove : null;
  const pinnedCopy = pinned ? deskMoveCopy(pinned) : null;
  const askPinned = (q: string) => {
    if (!pinned || !pinnedCopy) return;
    trackActivity('next_move_ask', { rule_key: pinned.key, persona: pinned.persona, surface: 'ask_panel', kind: pinned.kind, starter: q });
    void chat.send(`About my ${pinned.kind === 'setup' ? 'Desk Setup step' : NEXT_MOVE_LABEL} "${pinnedCopy.line}": ${q}`);
  };
  const pinnedDest = moveDestination(pinned);
  const pinnedTo = pinned && pinnedDest ? (pinned.cue ? withCue(pinnedDest, pinned.cue) : pinnedDest) : null;

  const submit = (attachments: ChatAttachment[] = []) => {
    const text = input.trim();
    if (!text) return;
    setInput('');
    void chat.send(text, { attachments });
  };

  const askSuggestion = (question: string) => {
    setInput('');
    void chat.send(question);
  };

  const emptyState = (
    <div className="flex h-full flex-col justify-center gap-6 px-5 py-8">
      <div className="space-y-2">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
          <Sparkles className="h-5 w-5 text-primary" />
        </div>
        <h2 className="text-lg font-semibold tracking-tight">How can I help?</h2>
        <p className="text-sm text-muted-foreground">
          {suggestions.pageTitle
            ? `I can see you're on ${suggestions.pageTitle}. Ask me anything about it — or about anywhere else in Cannafax.`
            : 'Ask me anything about menus, inventory, sharing, or analytics in Cannafax.'}
        </p>
      </div>

      <div className="space-y-1.5">
        <p className="px-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Suggested
        </p>
        {pickStarters(suggestions.questions.map((text) => ({ text }))).map(({ text: q }) => (
          <button
            key={q}
            onClick={() => askSuggestion(q)}
            className="w-full rounded-lg border border-border/60 px-3 py-2.5 text-left text-sm transition-colors hover:border-border hover:bg-muted/60"
          >
            {q}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-[440px] [&>button]:hidden"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          focusComposer();
        }}
      >
        {/* Header */}
        <div className="flex items-center gap-2 border-b border-border px-4 py-3">
          <Sparkles className="h-4 w-4 flex-shrink-0 text-primary" />
          <span className="text-sm font-semibold">Ask Cannafax</span>

          <div className="ml-auto flex items-center gap-0.5">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon-sm" aria-label="Conversation history">
                  <History className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground">
                  Chat and tasks
                </DropdownMenuLabel>
                <div className="px-1 pb-1"><ChatSearchInput value={chatQuery} onChange={setChatQuery} /></div>
                <DropdownMenuSeparator />
                {chatSearch.active ? (
                  <div className="max-h-72 overflow-y-auto">
                    <ChatSearchResults hits={chatSearch.hits} searching={chatSearch.searching}
                      onOpen={(id) => { setChatQuery(''); void chat.openThread(id); }} />
                  </div>
                ) : chat.loadingThreads ? (
                  <div className="flex items-center gap-2 px-2 py-3 text-xs text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Loading…
                  </div>
                ) : chat.threads.length === 0 ? (
                  <p className="px-2 py-3 text-xs text-muted-foreground">
                    No conversations yet.
                  </p>
                ) : (
                  <div className="max-h-72 overflow-y-auto">
                    {chat.threads.map((t) => (
                      <DropdownMenuItem
                        key={t.id}
                        className={cn(
                          'group flex items-start gap-2',
                          t.id === chat.activeThreadId && 'bg-muted',
                        )}
                        onSelect={() => void chat.openThread(t.id)}
                      >
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm">{t.title || 'Untitled'}</p>
                          <p className="text-xs text-muted-foreground">
                            {relativeTime(t.updated_at)}
                          </p>
                        </div>
                        <button
                          aria-label="Delete conversation"
                          className="mt-0.5 rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            void chat.deleteThread(t.id);
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </DropdownMenuItem>
                    ))}
                  </div>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="New conversation"
              onClick={() => {
                chat.startNewChat();
                setInput('');
                focusComposer();
              }}
            >
              <Plus className="h-4 w-4" />
            </Button>

            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Close assistant"
              onClick={() => setIsOpen(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Pinned suggestion (opened from Your Next Move™ / Desk Setup) */}
        {pinned && pinnedCopy && (
          <div className="border-b border-border bg-muted/40 px-4 py-3">
            <div className="flex items-start gap-2.5">
              <DeskMark className="mt-0.5 h-5 w-8 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium uppercase tracking-wider text-brand-text">
                  {pinned.kind === 'setup' ? 'Desk Setup' : NEXT_MOVE_LABEL}
                </p>
                <p className="text-sm leading-snug">{pinnedCopy.line}</p>
                {pinnedCopy.evidence && <p className="mt-0.5 text-xs text-muted-foreground">{pinnedCopy.evidence}</p>}
              </div>
            </div>
            {!hasAnswer(chat.messages) && <div className="mt-2.5 flex flex-wrap gap-1.5">
              {moveStarters(pinned).map((q) => (
                <button key={q} type="button" disabled={chat.isStreaming} onClick={() => askPinned(q)}
                  className="rounded-full border border-border/70 bg-card px-3 py-1 text-xs transition-colors hover:bg-muted disabled:opacity-50">
                  {q}
                </button>
              ))}
            </div>}
            <div className="mt-2.5 flex items-center justify-end gap-1">
              {pinned.kind === 'move' && (
                <Button size="sm" variant="ghost" className="text-muted-foreground" disabled={dismiss.isPending}
                  onClick={() => { dismiss.mutate({ ruleKey: pinned.key, surface: 'ask_panel' }); clearPinnedMove(); }}>
                  {NOT_NOW}
                </Button>
              )}
              {pinnedTo && pinnedCopy.button && (
                <Button asChild size="sm" variant="guide">
                  <Link to={pinnedTo} onClick={() => { if (pinned.kind === 'move') recordClick(pinned.key, 'ask_panel'); setIsOpen(false); }}>
                    {pinnedCopy.button}
                  </Link>
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Transcript */}
        {chat.loadingMessages ? (
          <div className="flex flex-1 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <AskCannafaxConversation
            messages={chat.messages}
            isStreaming={chat.isStreaming}
          toolStatus={chat.toolStatus}
            error={chat.error}
            onRetry={chat.retry}
            threadId={chat.activeThreadId}
            emptyState={emptyState}
          />
        )}

        {hasAnswer(chat.messages) && (
          <SuggestionTray
            className="px-4 pt-1"
            mode="follow-ups"
            suggestions={pickFollowUps(
              [...chat.messages].reverse().find((m) => m.role === 'assistant')?.content ?? '',
              (pinned ? moveStarters(pinned) : suggestions.questions).map((text) => ({ text })),
            )}
            hidden={chat.isStreaming}
            resetKey={chat.messages.length}
            onPick={(s) => askSuggestion(s.text)}
          />
        )}

        {/* Composer */}
        <AskCannafaxComposer
          ref={textareaRef}
          value={input}
          onChange={setInput}
          onSubmit={submit}
          onStop={chat.stop}
          isStreaming={chat.isStreaming}
        />
      </SheetContent>
    </Sheet>
  );
}